import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';

import { useUserSites, queryKeys } from '../hooks/useApi';
import { stripeApi, SiteData } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { calculateOverallDaysRemaining, getDaysRemainingColor, formatDaysRemaining, DaysRemainingInfo } from '../lib/billing-utils';
import { PRICING_PLANS } from '../config/pricing';

// Initialize Stripe
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

// Loading skeleton component
function BillingSkeleton() {
  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="h-6 bg-gray-200 rounded animate-pulse mb-4"></div>
      <div className="space-y-4">
        <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
        <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4"></div>
        <div className="h-4 bg-gray-200 rounded animate-pulse w-1/2"></div>
      </div>
    </div>
  );
}

// Error state component
function ErrorState({ error }: { error: Error }) {
  return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
      <div className="flex">
        <div className="flex-shrink-0">
          <div className="text-red-500 text-xl">⚠️</div>
        </div>
        <div className="ml-3">
          <h3 className="text-sm font-medium text-red-800">
            Failed to load billing information
          </h3>
          <p className="mt-1 text-sm text-red-600">
            {error.message || 'An error occurred while fetching billing data'}
          </p>
        </div>
      </div>
    </div>
  );
}

// Pricing Plan Component
function PricingPlan({ 
  name, 
  price, 
  interval, 
  features, 
  isPopular = false, 
  isCurrent = false,
  onSelect,
  loading = false
}: {
  name: string;
  price: string;
  interval: string;
  features: string[];
  isPopular?: boolean;
  isCurrent?: boolean;
  onSelect: () => void;
  loading?: boolean;
}) {
  return (
    <div className={`relative bg-white rounded-lg shadow-lg border-2 ${
      isPopular ? 'border-indigo-500' : 'border-gray-200'
    } ${isCurrent ? 'ring-2 ring-green-500' : ''}`}>
      {isPopular && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
          <span className="bg-indigo-500 text-white px-3 py-1 rounded-full text-xs font-medium">
            Most Popular
          </span>
        </div>
      )}
      
      <div className="p-6">
        <h3 className="text-xl font-semibold text-gray-900">{name}</h3>
        <div className="mt-4">
          <span className="text-4xl font-bold text-gray-900">${price}</span>
          <span className="text-gray-500">/{interval}</span>
        </div>
        
        <ul className="mt-6 space-y-3">
          {features.map((feature, index) => (
            <li key={index} className="flex items-center">
              <svg className="h-5 w-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span className="ml-3 text-gray-600">{feature}</span>
            </li>
          ))}
        </ul>
        
        <button
          onClick={onSelect}
          disabled={loading || isCurrent}
          className={`mt-8 w-full py-2 px-4 rounded-md font-medium ${
            isCurrent
              ? 'bg-green-100 text-green-800 cursor-not-allowed'
              : 'bg-indigo-600 text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2'
          } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {loading ? 'Processing...' : isCurrent ? 'Current Plan' : 'Select Plan'}
        </button>
      </div>
    </div>
  );
}

// Usage Chart Component
function UsageChart({ 
  usage, 
  limit, 
  sites 
}: { 
  usage: number; 
  limit: number; 
  sites: SiteData[] | undefined;
}) {
  const percentage = Math.min((usage / limit) * 100, 100);
  const isOverLimit = usage > limit;
  
          // Calculate days remaining - moved to DaysRemainingDisplay component
  
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Usage This Month</h3>
      
      <div className="space-y-4">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Votes</span>
          <span className={`font-medium ${isOverLimit ? 'text-red-600' : 'text-gray-900'}`}>
            {usage.toLocaleString()} / {limit.toLocaleString()}
          </span>
        </div>
        
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className={`h-2 rounded-full transition-all duration-300 ${
              isOverLimit ? 'bg-red-500' : percentage > 80 ? 'bg-yellow-500' : 'bg-green-500'
            }`}
            style={{ width: `${percentage}%` }}
          ></div>
        </div>
        
                       {/* Days Remaining Section */}
               <DaysRemainingDisplay sites={sites || []} currentPlan={sites?.some(site => site.plan === 'free') ? 'free' : 'paid'} />
        
        {isOverLimit && (
          <div className="text-sm text-red-600">
            You&apos;ve exceeded your monthly limit. Consider upgrading to Pro for unlimited usage.
          </div>
        )}
      </div>
    </div>
  );
}

// Helper component to render days remaining
function DaysRemainingDisplay({ sites, currentPlan }: { sites: SiteData[], currentPlan: string }) {
  const [daysRemainingInfo, setDaysRemainingInfo] = useState<DaysRemainingInfo | null>(null);
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    async function fetchDaysRemaining() {
      if (!sites || sites.length === 0) {
        setDaysRemainingInfo(null);
        setLoading(false);
        return;
      }

      try {
        const info = await calculateOverallDaysRemaining(sites);
        setDaysRemainingInfo(info);
      } catch (error) {
        console.error('Error calculating days remaining:', error);
        setDaysRemainingInfo(null);
      } finally {
        setLoading(false);
      }
    }

    fetchDaysRemaining();
  }, [sites]);

  if (loading) {
    return (
      <div className="border-t pt-4">
        <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
      </div>
    );
  }

  if (!daysRemainingInfo) return null;
  
  return (
    <div className="border-t pt-4">
      <div className="flex justify-between items-center">
        <span className="text-sm text-gray-600">
          {currentPlan === 'free' ? 'Free plan expires' : 'Next billing cycle'}
        </span>
        <span className={`text-sm font-medium ${getDaysRemainingColor(
          daysRemainingInfo.daysRemaining, 
          currentPlan === 'free'
        )}`}>
          {formatDaysRemaining(daysRemainingInfo)}
        </span>
      </div>
      <div className="text-xs text-gray-500 mt-1">
        {daysRemainingInfo.expirationDate.toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })}
      </div>
    </div>
  );
}

// Main billing page component
export function BillingPage() {
  const { data: sites, isLoading, error } = useUserSites();
  const [loading, setLoading] = useState(false);
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'annual'>('monthly');
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  // Handle successful checkout - detect success parameter and refresh data
  // NOTE: We deliberately exclude 'sites' from dependencies to avoid infinite loop
  // The sites value is captured when the effect runs based on URL changes only
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const success = searchParams.get('success');
    const canceled = searchParams.get('canceled');

    if (success === 'true') {
      // Show success message
      toast.success('Subscription activated! Your plan has been upgraded.');
      
      // Invalidate all relevant queries to fetch fresh data from the database
      queryClient.invalidateQueries({ queryKey: queryKeys.userSites() });
      
      // If we have sites, also invalidate their plan status
      // Using sites from closure - this won't cause infinite loop since sites is not in deps
      if (sites && sites.length > 0) {
        sites.forEach(site => {
          queryClient.invalidateQueries({ queryKey: queryKeys.planStatus(site.id) });
        });
      }
      
      // Clean up URL parameters
      const newUrl = window.location.pathname;
      navigate(newUrl, { replace: true });
      
      console.log('✅ Checkout successful - cache invalidated, data refreshed');
    } else if (canceled === 'true') {
      toast.error('Checkout canceled. Your plan was not changed.');
      
      // Clean up URL parameters
      const newUrl = window.location.pathname;
      navigate(newUrl, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, queryClient, navigate]);

  // Calculate total usage from sites
  const totalUsage = sites?.reduce((sum, site) => sum + site.monthlyCount, 0) || 0;
  const hasProSites = sites?.some(site => site.plan === 'pro') || false;
  const hasEnterpriseSites = sites?.some(site => site.plan === 'enterprise') || false;
  const currentPlan = hasEnterpriseSites ? 'enterprise' : hasProSites ? 'pro' : 'free';
  const freeLimit = PRICING_PLANS.free.limits.votesPerMonth;

  const pricingPlans = {
    monthly: [
      {
        name: PRICING_PLANS.free.name,
        price: PRICING_PLANS.free.price.monthly.toString(),
        interval: 'month',
        features: PRICING_PLANS.free.features.map(f => f.name),
        priceId: null
      },
      {
        name: PRICING_PLANS.pro.name,
        price: PRICING_PLANS.pro.price.monthly.toString(),
        interval: 'month',
        features: PRICING_PLANS.pro.features.filter(f => !f.name.includes('annual')).map(f => f.name),
        priceId: PRICING_PLANS.pro.stripeIds?.monthly
      },
      {
        name: PRICING_PLANS.enterprise.name,
        price: PRICING_PLANS.enterprise.price.monthly.toString(),
        interval: 'month',
        features: PRICING_PLANS.enterprise.features.filter(f => !f.name.includes('annual')).map(f => f.name),
        priceId: PRICING_PLANS.enterprise.stripeIds?.monthly
      }
    ],
    annual: [
      {
        name: PRICING_PLANS.free.name,
        price: PRICING_PLANS.free.price.annual.toString(),
        interval: 'month',
        features: PRICING_PLANS.free.features.map(f => f.name),
        priceId: null
      },
      {
        name: PRICING_PLANS.pro.name,
        price: PRICING_PLANS.pro.displayPrice.annualMonthly.replace('$', ''),
        interval: 'month',
        features: PRICING_PLANS.pro.features.map(f => f.name),
        priceId: PRICING_PLANS.pro.stripeIds?.annual
      },
      {
        name: PRICING_PLANS.enterprise.name,
        price: PRICING_PLANS.enterprise.displayPrice.annualMonthly.replace('$', ''),
        interval: 'month',
        features: PRICING_PLANS.enterprise.features.map(f => f.name),
        priceId: PRICING_PLANS.enterprise.stripeIds?.annual
      }
    ]
  };

  const handleUpgrade = async (priceId: string) => {
    if (!priceId) return;
    
    setLoading(true);
    try {
      const stripe = await stripePromise;
      if (!stripe) throw new Error('Stripe failed to load');

      // Get the first site for now (in a real app, you'd let user choose)
      const siteToUpgrade = sites?.[0];
      if (!siteToUpgrade) {
        alert('No sites found to upgrade. Please create a site first.');
        return;
      }

      // Check if site is already on a paid plan
      if (siteToUpgrade.plan !== 'free') {
        alert('This site is already on a paid plan. Please manage billing through the customer portal.');
        return;
      }

      // Create checkout session
      const response = await stripeApi.createCheckoutSession({
        siteId: siteToUpgrade.id,
        priceId,
        successUrl: `${window.location.origin}/dashboard/billing?success=true`,
        cancelUrl: `${window.location.origin}/dashboard/billing?canceled=true`,
        customerEmail: user?.email || 'user@example.com', // Get from auth context
      });

      const { sessionId } = response as { sessionId: string };
      
      // Redirect to Stripe Checkout
      const { error } = await stripe.redirectToCheckout({ sessionId });
      if (error) throw error;
      
    } catch (error) {
      console.error('Error creating checkout session:', error);
      alert('Failed to start checkout process. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleManageBilling = async () => {
    try {
      // Get customer ID from sites
      const proSite = sites?.find(site => site.stripeCustomerId);
      if (!proSite?.stripeCustomerId) {
        alert('No billing information found. Please upgrade a site first.');
        return;
      }

      const response = await stripeApi.createPortalSession({
        customerId: proSite.stripeCustomerId,
        returnUrl: `${window.location.origin}/dashboard/billing`,
      });

      const { url } = response as { url: string };
      window.location.href = url;
      
    } catch (error) {
      console.error('Error creating portal session:', error);
      alert('Failed to open billing portal. Please try again.');
    }
  };

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Billing & Usage</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage your subscription and view usage statistics
          </p>
        </div>
        <ErrorState error={error as Error} />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Billing & Usage</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage your subscription and view usage statistics
          </p>
        </div>
        <BillingSkeleton />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Billing & Usage</h1>
        <p className="mt-1 text-sm text-gray-600">
          Manage your subscription and view usage statistics
        </p>
      </div>

      {/* Current Plan & Usage */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Current Plan</h2>
          <div className="space-y-4">
            <div>
              <p className="text-2xl font-bold text-gray-900 capitalize">
                {currentPlan} Plan
              </p>
              <p className="text-sm text-gray-500">
                {currentPlan === 'free' && 'Up to 1,000 votes per month'}
                {currentPlan === 'pro' && 'Unlimited votes and advanced features'}
                {currentPlan === 'enterprise' && 'Enterprise features with dedicated support'}
              </p>
            </div>

            {/* Days Remaining for Current Plan */}
            <DaysRemainingDisplay sites={sites || []} currentPlan={currentPlan} />
            
            {currentPlan !== 'free' && (
              <button
                onClick={handleManageBilling}
                className="bg-gray-100 text-gray-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-200"
              >
                Manage Billing
              </button>
            )}
          </div>
        </div>

        <UsageChart usage={totalUsage} limit={freeLimit} sites={sites} />
      </div>

      {/* Pricing Plans */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Choose Your Plan</h2>
          <p className="text-gray-600">Select the plan that best fits your needs</p>
          
          {/* Billing Interval Toggle */}
          <div className="mt-6 flex justify-center">
            <div className="bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setBillingInterval('monthly')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  billingInterval === 'monthly'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingInterval('annual')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  billingInterval === 'annual'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Annual
                <span className="ml-1 text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                  Save 20%
                </span>
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {pricingPlans[billingInterval].map((plan, _index) => (
            <PricingPlan
              key={plan.name}
              name={plan.name}
              price={plan.price}
              interval={plan.interval}
              features={plan.features}
              isPopular={plan.name === 'Pro'}
              isCurrent={plan.name.toLowerCase() === currentPlan}
              onSelect={() => handleUpgrade(plan.priceId || '')}
              loading={loading}
            />
          ))}
        </div>
      </div>

      {/* Recent Invoices */}
      {currentPlan !== 'free' && (
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Recent Invoices</h2>
          <div className="text-center py-8 text-gray-500">
            <p>Invoice history will appear here after your first payment.</p>
            <button
              onClick={handleManageBilling}
              className="mt-2 text-indigo-600 hover:text-indigo-500 text-sm font-medium"
            >
              View all invoices →
            </button>
          </div>
        </div>
      )}
    </div>
  );
} 